import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/useAuth';
import { createBrand, getBrands, updateBrand } from '@/features/operations/api';
import type { BrandItem } from '@/features/operations/types';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

/// Names the operator is editing, keyed by brand id. Held as the strings they typed so a
/// half-finished edit is never saved on a keystroke.
type NameDrafts = Record<string, string>;

function buildNameDrafts(brands: BrandItem[]): NameDrafts {
  return brands.reduce<NameDrafts>((drafts, brand) => {
    drafts[brand.id] = brand.name;
    return drafts;
  }, {});
}

/**
 * Says what deactivating this brand will actually do, in models and devices, before the
 * operator does it. Deactivating never deletes anything and never touches the models
 * themselves, so reactivating restores exactly what was there.
 */
function describeConsequence(brand: BrandItem) {
  if (!brand.isActive) {
    return brand.activePhoneModelCount === 1
      ? 'Al reactivarla vuelve a ofrecerse 1 modelo.'
      : `Al reactivarla vuelven a ofrecerse ${brand.activePhoneModelCount} modelos.`;
  }

  if (brand.activePhoneModelCount === 0) {
    return 'No tiene modelos activos, así que hoy no aparece en el formulario público.';
  }

  return brand.activePhoneModelCount === 1
    ? 'Si la desactivás, 1 modelo deja de ofrecerse. No se borra: al reactivarla vuelve.'
    : `Si la desactivás, ${brand.activePhoneModelCount} modelos dejan de ofrecerse. No se borran: al reactivarla vuelven.`;
}

export function AdminBrandsPage() {
  const { token } = useAuth();

  const [brands, setBrands] = useState<BrandItem[]>([]);
  const [nameDrafts, setNameDrafts] = useState<NameDrafts>({});
  const [newBrandName, setNewBrandName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const loaded = await getBrands(token);
        setBrands(loaded);
        setNameDrafts(buildNameDrafts(loaded));
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar las marcas.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token]);

  if (!token) {
    return null;
  }

  const refresh = async () => {
    const loaded = await getBrands(token);
    setBrands(loaded);
    setNameDrafts(buildNameDrafts(loaded));
  };

  const run = async (action: () => Promise<void>, successText: string) => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await action();
      setSuccessMessage(successText);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = () =>
    run(async () => {
      if (!newBrandName.trim()) {
        throw new Error('Poné el nombre de la marca.');
      }

      await createBrand(token, { name: newBrandName.trim() });
      setNewBrandName('');
      await refresh();
    }, 'Marca creada.');

  const handleRename = (brand: BrandItem) =>
    run(async () => {
      const name = (nameDrafts[brand.id] || '').trim();

      if (!name) {
        throw new Error('El nombre no puede quedar vacío.');
      }

      if (name === brand.name) {
        throw new Error('El nombre no cambió.');
      }

      await updateBrand(token, brand.id, { name });
      await refresh();
    }, 'Marca renombrada.');

  const handleToggleActive = (brand: BrandItem) =>
    run(
      async () => {
        await updateBrand(token, brand.id, { isActive: !brand.isActive });
        await refresh();
      },
      brand.isActive ? 'Marca desactivada.' : 'Marca reactivada.',
    );

  const activeCount = brands.filter((brand) => brand.isActive).length;

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CONFIGURACIÓN COMERCIAL"
        title="Marcas del catálogo"
        description="Qué marcas se ofrecen en el formulario público de cotización. Desactivar una marca la saca del formulario sin borrar sus modelos ni afectar las solicitudes ya registradas."
        badge="Solo administración"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <SectionCard
            title="Agregar una marca"
            description="Se crea activa. Para dejar de ofrecerla, desactivala después."
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={newBrandName}
                  onChange={(event) => setNewBrandName(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <PrimaryButton onClick={handleCreate} disabled={isSaving}>
                  Agregar marca
                </PrimaryButton>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Marcas registradas"
            description="Las marcas no se borran: se desactivan, para que los modelos y equipos que las nombran sigan siendo legibles."
          >
            {brands.length === 0 ? (
              <Alert severity="info">
                Todavía no hay marcas cargadas. Sin marcas activas con modelos, el formulario
                público no puede cotizar ningún equipo.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {activeCount === 0 ? (
                  <Alert severity="warning">
                    No hay ninguna marca activa. El formulario público no puede cotizar ningún
                    equipo hasta que actives al menos una.
                  </Alert>
                ) : null}

                {brands.map((brand, index) => (
                  <Box key={brand.id}>
                    {index > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={`Nombre de ${brand.name}`}
                          value={nameDrafts[brand.id] ?? ''}
                          onChange={(event) =>
                            setNameDrafts((drafts) => ({
                              ...drafts,
                              [brand.id]: event.target.value,
                            }))
                          }
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={brand.isActive ? 'Se ofrece' : 'Retirada'}
                            color={brand.isActive ? 'success' : 'default'}
                            variant={brand.isActive ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {brand.activePhoneModelCount}/{brand.phoneModelCount} modelos ·{' '}
                            {brand.deviceCount} equipos
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <PrimaryButton
                            size="small"
                            onClick={() => handleRename(brand)}
                            disabled={isSaving}
                          >
                            Guardar nombre
                          </PrimaryButton>
                          <PrimaryButton
                            size="small"
                            color="secondary"
                            onClick={() => handleToggleActive(brand)}
                            disabled={isSaving}
                          >
                            {brand.isActive ? 'Desactivar' : 'Reactivar'}
                          </PrimaryButton>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {describeConsequence(brand)} Última modificación:{' '}
                          {new Date(brand.updatedAt).toLocaleString('es-UY')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </>
      )}
    </Stack>
  );
}
